import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LIVESTOCK_SUMMARY_INDEX } from '../../../router/routes';
import { formatDateValue, route } from '../../../services/utils';
import { LinkComponent } from '../../../components/form/elements/link';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import css from './summary-view.module.css';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../services/services';
import FatAndProteinSummary from './components/metabolism/fat-protein-summary';
import LowYieldSummary from './components/metabolism/low-yield-summary';
import LowFatInMilkSummary from './components/metabolism/low-milk-fat-summary';
import DayYieldSummary from './components/metabolism/day-yield-summary';
import HighFatInMilkSummary from './components/metabolism/high-milk-fat-summary';
import { MetabolismInfo } from '../../../interfaces/livestock-metabolism';

export enum TabKey {
  DAIRY = 'dairy',
}

export default function ViewMetabolism() {
  const { livestockMetabolismService, livestockSummaryService } = useService();
  const { t } = useTranslation();
  const { farmId, id } = useParams<{ farmId: string; id: string }>();

  const SummaryType = {
    MILK_PROTEIN: t('pages.livestock.summary.header.summary_protein'),
    MILK_FAT: t('pages.livestock.summary.header.summary_fat'),
    LOW_MILK_FAT: t('pages.livestock.summary.header.summary_low_fat'),
    HIGH_MILK_FAT: t('pages.livestock.summary.header.summary_high_fat'),
    LOW_YIELD_COW: t('pages.livestock.summary.header.summary_yield_low'),
    DAY_YIELD_COW: t('pages.livestock.summary.header.summary_yield'),
  };

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [inputValue, setInputValue] = useState<number>(0);
  const [metabolismData, setMetabolismData] = useState<MetabolismInfo>({
    fatAndProtein: [],
    lowYieldData: [],
    lowFatData: [],
    milkYieldData: [],
    highFatData: [],
    avgFat: null,
    avgProtein: null,
  });

  useEffect(() => {
    livestockMetabolismService
      .summaryType(farmId, id)
      .subscribe((response: any) => {
        setTitle(response.summary);
        setDate(response.current_date);

        livestockSummaryService.getConfigurations(farmId, encodeURIComponent(response.summary)).subscribe(config => {
          setInputValue(config);
        });
      })
  }, [livestockMetabolismService, livestockSummaryService, farmId, id]);

  useEffect(() => {
    livestockMetabolismService.summaryInfo(farmId, date, encodeURIComponent(title)).subscribe(
      (response: MetabolismInfo) => {
        setMetabolismData(response);
      })
  }, [livestockMetabolismService, farmId, date, title]);

  const handleInputChange = (event: any) => {
    const value = event.target.value.replace(/[^\d.,]/g, '');
    setInputValue(value);

    livestockSummaryService.saveConfigurations(farmId, encodeURIComponent(title), value).subscribe();
  };

  return (
    <div className={css['field-info']}>
      <div className={css['header']}>
        <LinkComponent
          label="back"
          icon={faLongArrowLeft}
          linkTo={route(LIVESTOCK_SUMMARY_INDEX, {
            farmId: farmId,
            tab: TabKey.DAIRY,
          })}
        />
        <h2>{title ? title : ''}</h2>
      </div>

      <div className={css['top-bar']}>
        <div>
          <span>{t('pages.livestock.summary.section.control_date')}</span>
          <span className={css['strong']}>: {date && formatDateValue(date)} </span>
          {(title === SummaryType.LOW_YIELD_COW ||
            title === SummaryType.LOW_MILK_FAT) && (
              <p>
                {t('pages.livestock.summary.view.percentage_goal')}
                <input
                  className={css['goal-picker']}
                  type="text"
                  value={inputValue}
                  onInput={handleInputChange}
                />
              </p>
            )}
        </div>
      </div>

      {(title === SummaryType.MILK_FAT ||
        title === SummaryType.MILK_PROTEIN) && (
          <FatAndProteinSummary metabolismData={metabolismData} />
        )}

      {title === SummaryType.LOW_YIELD_COW && (
        <LowYieldSummary metabolismData={metabolismData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.LOW_MILK_FAT && (
        <LowFatInMilkSummary metabolismData={metabolismData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.DAY_YIELD_COW && (
        <DayYieldSummary metabolismData={metabolismData} title={title} />
      )}

      {title === SummaryType.HIGH_MILK_FAT && (
        <HighFatInMilkSummary metabolismData={metabolismData} title={title} />
      )}
    </div>
  );
}
